import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  createConvenio,
  deleteConvenio,
  getConvenioStats,
  getConvenios,
  updateConvenio,
} from '../lib/api';
import { todayYmdInClinic } from '../lib/clinicTime';
import { toast } from 'sonner';
import { BarChart3, Handshake, Pencil, Plus, Trash2 } from 'lucide-react';

function monthRangeDefaults() {
  const today = todayYmdInClinic();
  const [y, m] = today.split('-').map(Number);
  const date_from = `${y}-${String(m).padStart(2, '0')}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  const date_to = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { date_from, date_to };
}

export default function AdminConvenios() {
  const [convenios, setConvenios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ nombre: '', empresa: '', is_active: true });
  const [statsRange, setStatsRange] = useState(monthRangeDefaults);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const loadConvenios = useCallback(async () => {
    try {
      const data = await getConvenios(false);
      setConvenios(Array.isArray(data) ? data : []);
    } catch {
      toast.error('No se pudieron cargar los convenios');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await getConvenioStats(statsRange);
      setStats(data);
    } catch {
      toast.error('No se pudo cargar el reporte');
    } finally {
      setStatsLoading(false);
    }
  }, [statsRange]);

  useEffect(() => {
    loadConvenios();
  }, [loadConvenios]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const resetForm = () => {
    setFormData({ nombre: '', empresa: '', is_active: true });
    setEditing(null);
  };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (conv) => {
    setEditing(conv);
    setFormData({
      nombre: conv.nombre || '',
      empresa: conv.empresa || '',
      is_active: conv.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nombre = formData.nombre.trim();
    const empresa = formData.empresa.trim();
    if (!nombre || !empresa) {
      toast.error('Nombre y empresa son obligatorios');
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        await updateConvenio(editing.id, {
          nombre,
          empresa,
          is_active: editing.is_system_default ? true : formData.is_active,
        });
        toast.success('Convenio actualizado');
      } else {
        await createConvenio({ nombre, empresa, is_active: formData.is_active });
        toast.success('Convenio creado');
      }
      setDialogOpen(false);
      resetForm();
      await loadConvenios();
      await loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (conv) => {
    if (conv.is_system_default) return;
    if (!window.confirm(`¿Eliminar el convenio «${conv.nombre}»?`)) return;
    try {
      await deleteConvenio(conv.id);
      toast.success('Convenio eliminado');
      await loadConvenios();
      await loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground animate-pulse">
        Cargando convenios…
      </div>
    );
  }

  return (
    <div data-testid="admin-convenios" className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-2">
            <Handshake className="w-8 h-8 text-primary" />
            Convenios
          </h1>
          <p className="text-muted-foreground mt-1">
            Catálogo de convenios empresariales. El valor por defecto es «No aplica».
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full gap-1" onClick={openCreate}>
              <Plus className="w-4 h-4" />
              Nuevo convenio
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? 'Editar convenio' : 'Nuevo convenio'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="conv-nombre">Nombre del convenio</Label>
                <Input
                  id="conv-nombre"
                  className="mt-1"
                  value={formData.nombre}
                  onChange={(e) => setFormData((p) => ({ ...p, nombre: e.target.value }))}
                  disabled={editing?.is_system_default}
                />
              </div>
              <div>
                <Label htmlFor="conv-empresa">Empresa</Label>
                <Input
                  id="conv-empresa"
                  className="mt-1"
                  value={formData.empresa}
                  onChange={(e) => setFormData((p) => ({ ...p, empresa: e.target.value }))}
                />
              </div>
              {!editing?.is_system_default ? (
                <div className="flex items-center gap-2">
                  <Switch
                    id="conv-active"
                    checked={formData.is_active}
                    onCheckedChange={(v) => setFormData((p) => ({ ...p, is_active: v }))}
                  />
                  <Label htmlFor="conv-active">Activo</Label>
                </div>
              ) : null}
              <Button type="submit" className="w-full rounded-full" disabled={submitting}>
                {submitting ? 'Guardando…' : 'Guardar'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle>Catálogo</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Empresa</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {convenios.map((conv) => (
                <TableRow key={conv.id}>
                  <TableCell className="font-medium">
                    {conv.nombre}
                    {conv.is_system_default ? (
                      <span className="ml-2 text-xs text-muted-foreground">(sistema)</span>
                    ) : null}
                  </TableCell>
                  <TableCell>{conv.empresa}</TableCell>
                  <TableCell>{conv.is_active ? 'Activo' : 'Inactivo'}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button type="button" variant="ghost" size="sm" onClick={() => openEdit(conv)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    {!conv.is_system_default ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => handleDelete(conv)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Pacientes atendidos por convenio
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Solo citas completadas. Un paciente se cuenta una vez por mes y convenio.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label htmlFor="stats-from">Fecha inicio</Label>
              <Input
                id="stats-from"
                type="date"
                className="mt-1 w-40"
                value={statsRange.date_from}
                onChange={(e) => setStatsRange((p) => ({ ...p, date_from: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="stats-to">Fecha fin</Label>
              <Input
                id="stats-to"
                type="date"
                className="mt-1 w-40"
                value={statsRange.date_to}
                onChange={(e) => setStatsRange((p) => ({ ...p, date_to: e.target.value }))}
              />
            </div>
            <Button type="button" variant="outline" className="rounded-full" onClick={loadStats} disabled={statsLoading}>
              {statsLoading ? 'Cargando…' : 'Actualizar reporte'}
            </Button>
          </div>

          {stats?.totals_by_convenio?.length > 0 ? (
            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Convenio</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead className="text-right">Pacientes (rango)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.totals_by_convenio.map((row) => (
                    <TableRow key={row.convenio_id}>
                      <TableCell>{row.convenio_nombre}</TableCell>
                      <TableCell>{row.convenio_empresa}</TableCell>
                      <TableCell className="text-right font-medium">{row.patient_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Sin datos en el rango seleccionado.</p>
          )}

          {stats?.rows?.length > 0 ? (
            <div className="rounded-lg border overflow-x-auto">
              <p className="text-sm font-medium px-4 pt-3">Desglose mensual</p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mes</TableHead>
                    <TableHead>Convenio</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead className="text-right">Pacientes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.rows.map((row, idx) => (
                    <TableRow key={`${row.month}-${row.convenio_id}-${idx}`}>
                      <TableCell>{row.month}</TableCell>
                      <TableCell>{row.convenio_nombre}</TableCell>
                      <TableCell>{row.convenio_empresa}</TableCell>
                      <TableCell className="text-right">{row.patient_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
